import { useCallback, useEffect, useRef } from 'react';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import { VaroCategories } from 'shared/analytics/types/categories';
import { useComponentAnalytics } from 'shared/hooks/analytics/useComponentAnalytics';
import { VXLNode } from 'shared/renderer/types';

const VISIBLE_THRESHOLD = 0.5;
const THROTTLE_MS = 150;

interface TileLayout {
  y: number;
  height: number;
}

const useHomeTileViewportTracking = (tiles: VXLNode[]) => {
  const { logComponentViewed } = useComponentAnalytics();
  const logComponentViewedRef = useRef(logComponentViewed);
  const viewedTilesRef = useRef(new Set<string | number>());
  const tileLayoutsRef = useRef(new Map<string | number, TileLayout>());
  const scrollOffsetRef = useRef(0);
  const containerOffsetRef = useRef<number | null>(null);
  const tilesRef = useRef(tiles);
  const viewportHeightRef = useRef<number | null>(null);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCompleteRef = useRef(false);
  const layoutHandlersRef = useRef(
    new Map<string | number, (event: LayoutChangeEvent) => void>(),
  );

  useEffect(() => {
    logComponentViewedRef.current = logComponentViewed;
  }, [logComponentViewed]);

  useEffect(() => {
    tilesRef.current = tiles;

    const currentIds = new Set(tiles.map((t) => t.id));
    viewedTilesRef.current.forEach((id) => {
      if (!currentIds.has(id)) {
        viewedTilesRef.current.delete(id);
      }
    });
    tileLayoutsRef.current.forEach((_, id) => {
      if (!currentIds.has(id)) {
        tileLayoutsRef.current.delete(id);
      }
    });
    layoutHandlersRef.current.forEach((_, id) => {
      if (!currentIds.has(id)) {
        layoutHandlersRef.current.delete(id);
      }
    });

    isCompleteRef.current = tiles.every((tile) =>
      viewedTilesRef.current.has(tile.id),
    );
  }, [tiles]);

  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
    };
  }, []);

  const checkVisibility = useCallback(() => {
    if (isCompleteRef.current) {
      return;
    }

    const viewportHeight = viewportHeightRef.current;
    const containerOffset = containerOffsetRef.current;
    if (viewportHeight === null || containerOffset === null) {
      return;
    }

    const currentTiles = tilesRef.current;
    const allViewed = currentTiles.every((tile) =>
      viewedTilesRef.current.has(tile.id),
    );
    if (allViewed) {
      isCompleteRef.current = true;
      return;
    }

    const scrollY = scrollOffsetRef.current;
    const viewportBottom = scrollY + viewportHeight;

    currentTiles.forEach((tile) => {
      if (viewedTilesRef.current.has(tile.id)) {
        return;
      }

      const layout = tileLayoutsRef.current.get(tile.id);
      if (!layout) {
        return;
      }

      const tileTop = layout.y + containerOffset;
      const tileBottom = tileTop + layout.height;
      const visibleTop = Math.max(scrollY, tileTop);
      const visibleBottom = Math.min(viewportBottom, tileBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleRatio =
        layout.height > 0 ? visibleHeight / layout.height : 0;

      if (visibleRatio >= VISIBLE_THRESHOLD) {
        viewedTilesRef.current.add(tile.id);

        logComponentViewedRef.current({
          category: VaroCategories.VARO_HOME,
          component_type: 'name' in tile ? tile.name : 'tile',
          component_name: String(tile.id),
        });
      }
    });

    isCompleteRef.current = currentTiles.every((tile) =>
      viewedTilesRef.current.has(tile.id),
    );
  }, []);

  const scheduleCheck = useCallback(() => {
    if (throttleTimerRef.current) {
      return;
    }
    throttleTimerRef.current = setTimeout(() => {
      throttleTimerRef.current = null;
      checkVisibility();
    }, THROTTLE_MS);
  }, [checkVisibility]);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetRef.current = event.nativeEvent.contentOffset.y;
      viewportHeightRef.current = event.nativeEvent.layoutMeasurement.height;
      if (isCompleteRef.current) {
        return;
      }
      scheduleCheck();
    },
    [scheduleCheck],
  );

  const onScrollViewLayout = useCallback(
    (event: LayoutChangeEvent) => {
      viewportHeightRef.current = event.nativeEvent.layout.height;
      checkVisibility();
    },
    [checkVisibility],
  );

  const getLayoutHandler = useCallback(
    (tileId: string | number) => {
      const existing = layoutHandlersRef.current.get(tileId);
      if (existing) {
        return existing;
      }
      const handler = (event: LayoutChangeEvent) => {
        const { y, height } = event.nativeEvent.layout;
        tileLayoutsRef.current.set(tileId, { y, height });
        checkVisibility();
      };
      layoutHandlersRef.current.set(tileId, handler);
      return handler;
    },
    [checkVisibility],
  );

  const onContainerLayout = useCallback(
    (event: LayoutChangeEvent) => {
      containerOffsetRef.current = event.nativeEvent.layout.y;
      checkVisibility();
    },
    [checkVisibility],
  );

  return { onScroll, onScrollViewLayout, getLayoutHandler, onContainerLayout };
};

export default useHomeTileViewportTracking;
